package com.evoapps.evoshift.domain.requests

import com.evoapps.evoshift.domain.market.payroll.LeaveKind
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.WorkRequestKind
import kotlinx.datetime.minus

/** SH-223: who pays for the time of a request (Polish law; docs/requests.md has the sources). */
enum class Payer {
    /** The employer pays (it is in the labour cost). */
    EMPLOYER,
    /** ZUS pays a benefit; the employer pays nothing for those days. */
    ZUS,
    /** Nobody: unpaid. */
    NOBODY
}

/** SH-223: how the new-request chooser groups the types. */
enum class RequestGroup { LEAVE, FAMILY, HEALTH, WORK }

/** Any request type: an absence (stored in `absences`) or work (stored in `work_requests`). */
sealed interface RequestType {
    val name: String
    data class Leave(val type: AbsenceType) : RequestType { override val name: String get() = type.name }
    data class Work(val kind: WorkRequestKind) : RequestType { override val name: String get() = kind.name }
}

/**
 * SH-223: what each request type means. [payShare] is the share of the normal pay the [payer]
 * pays (sick leave: 80% by the employer for the first 33 days of the year, then ZUS; see
 * PolishLeavePay). [blocksScheduling]: approved, the person is away and their shifts are freed.
 */
data class RequestTypeInfo(
    val type: RequestType,
    val group: RequestGroup,
    val payer: Payer,
    val payShare: Double,
    val blocksScheduling: Boolean,
    /** May cover part of one day (hours). */
    val allowsHours: Boolean = false,
    /** Must cover part of one day (hours). */
    val requiresHours: Boolean = false,
    /** A place from the workplace's list is asked for. */
    val asksPlace: Boolean = false,
    /** An attachment (a scan of the certificate) makes sense. */
    val takesAttachment: Boolean = false
)

object RequestCatalog {

    private fun leave(
        type: AbsenceType, group: RequestGroup, payer: Payer, share: Double,
        hours: Boolean = false, attachment: Boolean = false
    ) = RequestTypeInfo(RequestType.Leave(type), group, payer, share, blocksScheduling = true, allowsHours = hours, takesAttachment = attachment)

    /** Every type, in the chooser's order. */
    val all: List<RequestTypeInfo> = listOf(
        leave(AbsenceType.VACATION, RequestGroup.LEAVE, Payer.EMPLOYER, 1.0),
        leave(AbsenceType.ON_DEMAND, RequestGroup.LEAVE, Payer.EMPLOYER, 1.0),
        leave(AbsenceType.OCCASIONAL_LEAVE, RequestGroup.LEAVE, Payer.EMPLOYER, 1.0, attachment = true),
        leave(AbsenceType.TIME_OFF_IN_LIEU, RequestGroup.LEAVE, Payer.EMPLOYER, 1.0, hours = true),
        leave(AbsenceType.BLOOD_DONATION, RequestGroup.LEAVE, Payer.EMPLOYER, 1.0, attachment = true),
        leave(AbsenceType.FORCE_MAJEURE, RequestGroup.LEAVE, Payer.EMPLOYER, 0.5, hours = true),
        leave(AbsenceType.UNPAID_LEAVE, RequestGroup.LEAVE, Payer.NOBODY, 0.0),
        leave(AbsenceType.CHILDCARE, RequestGroup.FAMILY, Payer.EMPLOYER, 1.0, hours = true),
        leave(AbsenceType.CARE_ALLOWANCE, RequestGroup.FAMILY, Payer.ZUS, 0.8, attachment = true),
        leave(AbsenceType.CARER_LEAVE, RequestGroup.FAMILY, Payer.NOBODY, 0.0),
        leave(AbsenceType.MATERNITY, RequestGroup.FAMILY, Payer.ZUS, 1.0),
        leave(AbsenceType.PATERNITY, RequestGroup.FAMILY, Payer.ZUS, 1.0),
        leave(AbsenceType.PARENTAL, RequestGroup.FAMILY, Payer.ZUS, 0.7),
        leave(AbsenceType.CHILDCARE_LEAVE, RequestGroup.FAMILY, Payer.NOBODY, 0.0),
        leave(AbsenceType.SICK_LEAVE, RequestGroup.HEALTH, Payer.EMPLOYER, 0.8, attachment = true),
        leave(AbsenceType.QUARANTINE, RequestGroup.HEALTH, Payer.EMPLOYER, 0.8, attachment = true),
        RequestTypeInfo(RequestType.Work(WorkRequestKind.REMOTE_WORK), RequestGroup.WORK, Payer.EMPLOYER, 1.0, blocksScheduling = false, allowsHours = true, asksPlace = true),
        RequestTypeInfo(RequestType.Work(WorkRequestKind.WORK_EVENT), RequestGroup.WORK, Payer.EMPLOYER, 1.0, blocksScheduling = false, allowsHours = true, asksPlace = true),
        RequestTypeInfo(RequestType.Work(WorkRequestKind.OVERTIME), RequestGroup.WORK, Payer.EMPLOYER, 1.5, blocksScheduling = false, allowsHours = true, requiresHours = true)
    )

    fun of(type: AbsenceType): RequestTypeInfo = all.first { (it.type as? RequestType.Leave)?.type == type }
    fun of(kind: WorkRequestKind): RequestTypeInfo = all.first { (it.type as? RequestType.Work)?.kind == kind }

    /**
     * How the payroll estimate treats an approved absence of [type] (PolishLeavePay): sick leave by
     * its own rules (quarantine and isolation are paid as sickness), annual leave as holiday pay,
     * the rest as paid, half-paid, unpaid or ZUS-paid time off.
     */
    fun leaveKind(type: AbsenceType, sickPayFull: Boolean = false): LeaveKind = when (type) {
        AbsenceType.SICK_LEAVE, AbsenceType.QUARANTINE -> if (sickPayFull) LeaveKind.SICK_FULL_PAY else LeaveKind.SICK
        AbsenceType.VACATION, AbsenceType.ON_DEMAND -> LeaveKind.VACATION
        AbsenceType.OCCASIONAL_LEAVE, AbsenceType.BLOOD_DONATION, AbsenceType.TIME_OFF_IN_LIEU, AbsenceType.CHILDCARE -> LeaveKind.PAID_LEAVE
        AbsenceType.FORCE_MAJEURE -> LeaveKind.HALF_PAID_LEAVE
        AbsenceType.UNPAID_LEAVE, AbsenceType.CARER_LEAVE, AbsenceType.CHILDCARE_LEAVE -> LeaveKind.UNPAID_LEAVE
        AbsenceType.MATERNITY, AbsenceType.PATERNITY, AbsenceType.PARENTAL, AbsenceType.CARE_ALLOWANCE -> LeaveKind.BENEFIT_LEAVE
    }

    /** The types an employee picks from (all of them; the order is [all]'s). */
    val choosable: List<RequestTypeInfo> get() = all
}

/** SH-223: a request type the workplace switched off, or not offered on the person's contract that day. */
class RequestNotAllowedException(val typeName: String) : Exception("Request type $typeName is not offered here")

/**
 * SH-223: which request types a person is offered: the workplace's rule of each type
 * (RequestSettings.types) against the contract valid on the request's day (pay terms read by the
 * person themselves; none known: an employment contract, as the payroll reads it).
 */
object RequestEligibility {
    fun contractOn(financial: com.evoapps.evoshift.domain.model.FinancialEngagement?, date: String): com.evoapps.evoshift.domain.model.SystemContractType? =
        financial?.let { runCatching { it.contractOn(date).contract }.getOrNull() }

    /** The contract's stored id that day (a SystemContractType name, or one of the owner's contract types' ids). */
    fun contractTypeIdOn(financial: com.evoapps.evoshift.domain.model.FinancialEngagement?, date: String): String? =
        financial?.let { runCatching { it.contractOn(date).contractTypeId }.getOrNull() }?.takeIf { it.isNotBlank() }

    fun allows(settings: com.evoapps.evoshift.domain.model.RequestSettings, type: RequestType, financial: com.evoapps.evoshift.domain.model.FinancialEngagement?, date: String): Boolean =
        settings.allows(type.name, contractOn(financial, date), contractTypeIdOn(financial, date))

    /** The chooser's types for that contract and day, in [RequestCatalog.all]'s order. */
    fun offered(settings: com.evoapps.evoshift.domain.model.RequestSettings, financial: com.evoapps.evoshift.domain.model.FinancialEngagement?, date: String): List<RequestTypeInfo> =
        RequestCatalog.all.filter { allows(settings, it.type, financial, date) }
}

/**
 * SH-223: the order of the new-request picker: the popular types on top (the person's three most
 * sent in the last 12 months, then the usual ones: annual leave, leave on demand, sick leave,
 * remote work, up to [TOP_SIZE]), then the rest: the Labour Code's leave, special leave and blood
 * donation, unpaid and ZUS-paid leave, then overtime and work events.
 */
object RequestOrdering {
    const val TOP_SIZE = 4
    private const val OWN_TOP = 3

    val POPULAR = listOf("VACATION", "ON_DEMAND", "SICK_LEAVE", "REMOTE_WORK")

    /** Everything after the top group, in this order. */
    val REST = listOf(
        "FORCE_MAJEURE", "CHILDCARE", "CARER_LEAVE", "TIME_OFF_IN_LIEU", "OCCASIONAL_LEAVE", "BLOOD_DONATION", "UNPAID_LEAVE",
        "MATERNITY", "PATERNITY", "PARENTAL", "CHILDCARE_LEAVE", "QUARANTINE", "CARE_ALLOWANCE", "OVERTIME", "WORK_EVENT"
    )

    data class Ordered(val top: List<RequestTypeInfo>, val rest: List<RequestTypeInfo>)

    /**
     * [offered] split and ordered; [history] the names and start days ("YYYY-MM-DD") of the
     * person's requests, [today] for the 12 months.
     */
    fun of(offered: List<RequestTypeInfo>, history: List<Pair<String, String>>, today: kotlinx.datetime.LocalDate): Ordered {
        val since = today.minus(kotlinx.datetime.DatePeriod(months = 12)).toString()
        val byName = offered.associateBy { it.type.name }
        val own = history.filter { (_, day) -> day >= since }.groupingBy { it.first }.eachCount()
            .entries.sortedWith(compareByDescending<Map.Entry<String, Int>> { it.value }.thenBy { POPULAR.indexOf(it.key).let { i -> if (i < 0) 99 else i } })
            .map { it.key }.filter { it in byName }.take(OWN_TOP)
        val top = (own + POPULAR.filter { it in byName }).distinct().take(TOP_SIZE)
        val order = POPULAR + REST
        val rest = offered.filter { it.type.name !in top }.sortedBy { order.indexOf(it.type.name).let { i -> if (i < 0) order.size else i } }
        return Ordered(top.map { byName.getValue(it) }, rest)
    }
}

/**
 * SH-223: how the workplace settings group the request types (as the new-request picker orders
 * them): the Labour Code's leave, time off for occasions and care, sickness and ZUS benefits,
 * unpaid leave, work.
 */
enum class RequestSettingsGroup(val typeNames: List<String>) {
    LABOUR_CODE_LEAVE(listOf("VACATION", "ON_DEMAND", "TIME_OFF_IN_LIEU")),
    OCCASIONS_AND_CARE(listOf("OCCASIONAL_LEAVE", "BLOOD_DONATION", "CHILDCARE", "CARER_LEAVE", "FORCE_MAJEURE")),
    SICKNESS_AND_ZUS(listOf("SICK_LEAVE", "QUARANTINE", "CARE_ALLOWANCE", "MATERNITY", "PATERNITY", "PARENTAL")),
    UNPAID(listOf("UNPAID_LEAVE", "CHILDCARE_LEAVE")),
    WORK(listOf("REMOTE_WORK", "WORK_EVENT", "OVERTIME"));

    /** The group's types from [RequestCatalog.all]. */
    val types: List<RequestTypeInfo> get() = typeNames.map { name -> RequestCatalog.all.first { it.type.name == name } }

    companion object {
        fun of(typeName: String): RequestSettingsGroup = entries.first { typeName in it.typeNames }
    }
}
